import type React from "react";
import { useEffect, useState } from "react";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
	Button,
	Stepper,
	Step,
	StepLabel,
	type Theme,
	type DialogProps,
	Snackbar,
	CircularProgress,
	StepConnector,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { makeStyles, withStyles } from "@mui/styles";
import Alert from "@mui/lab/Alert";
import { ErrorModal } from "@dexteel/mesf-core";
import { StartDateStep } from "./StartDateStep";
import { PeriodStep } from "./PeriodStep";
import { CustomRecurrenceStep } from "./CustomRecurrenceStep";
import { TimeService, getMomentTz } from "@dexteel/mesf-core";

const moment = getMomentTz();

const ColorlibConnector = withStyles({
	alternativeLabel: {
		top: 22,
	},
	active: {
		"& $line": {
			backgroundColor: "#3f51b5",
		},
	},
	completed: {
		"& $line": {
			backgroundColor: "#4caf50",
		},
	},
	line: {
		height: 3,
		border: 0,
		backgroundColor: "#eaeaf0",
		borderRadius: 1,
	},
})(StepConnector);

const useStyles = makeStyles((theme: Theme) => ({
	stepper: {
		background: "none",
	},
	stepIcon: {
		color: theme.palette.primary.main,
		background: theme.palette.background.paper,
		"&$completedStepIcon": {
			color: theme.palette.success.main,
		},
	},
	completedStepIcon: {},
	disabledStep: {
		color: "#bdbdbd !important",
		"& .MuiStepLabel-label": {
			color: "#bdbdbd !important",
		},
		"& .MuiStepIcon-root": {
			color: "#bdbdbd !important",
		},
	},
	dialogTitle: {
		padding: "5px 24px 10px",
	},
	dialogContent: {
		padding: "10px 24px",
	},
	dialogActions: {
		margin: "15px 0",
		padding: "0 10px",
	},
	title: {
		margin: "10px 0",
		fontWeight: 600,
		userSelect: "none",
		marginLeft: 5,
	},
	button: {
		margin: theme.spacing(1),
	},
}));

const steps = ["Select Start Date", "Select Period", "Custom Recurrence"];

interface CreateScheduleWizardProps {
	show: boolean;
	onHide: (onHide: boolean) => void;
	onSave: (cronExpression: string, scheduleStart: Date) => void;
	initialCronExpression?: string;
	initialScheduleStart?: Date | null;
}

export const CreateScheduleWizard: React.FC<CreateScheduleWizardProps> = ({
	show,
	onHide,
	onSave,
	initialCronExpression,
	initialScheduleStart,
}) => {
	const classes = useStyles();
	const [activeStep, setActiveStep] = useState(0);
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth] = useState<DialogProps["maxWidth"]>("md");
	const [startDate, setStartDate] = useState<Date | null>(new Date());
	const [selectedPeriod, setSelectedPeriod] = useState<string>("daily");
	const [timeValue, setTimeValue] = useState<Date | null>(new Date());
	const [cronExpression, setCronExpression] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
	const [snackbarMessage, setSnackbarMessage] = useState<string>("");
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [customStepDisabled, setCustomStepDisabled] = useState<boolean>(true);
	const serverTimezone = TimeService.getInstance().getServerTimeZone();

	useEffect(() => {
		if (show) {
			// Use provided initial values or defaults
			setStartDate(initialScheduleStart || new Date());

			// Extract time from CRON if available
			if (initialCronExpression) {
				const cronParts = initialCronExpression.trim().split(/\s+/);
				if (cronParts.length >= 2) {
					const minutes = Number.parseInt(cronParts[0]);
					const hours = Number.parseInt(cronParts[1]);

					if (!isNaN(minutes) && !isNaN(hours)) {
						// Use server timezone to ensure consistency
						const newTime = moment()
							.tz(serverTimezone)
							.hours(hours)
							.minutes(minutes)
							.seconds(0)
							.milliseconds(0)
							.toDate();

						setTimeValue(newTime);
						setCronExpression(initialCronExpression);

						// Determine period type based on CRON
						if (
							cronParts[2] === "*" &&
							cronParts[3] === "*" &&
							cronParts[4] === "*"
						) {
							setSelectedPeriod("daily");
						} else if (
							cronParts[2] === "*" &&
							cronParts[3] === "*" &&
							/^\d+$/.test(cronParts[4])
						) {
							setSelectedPeriod("weekly");
						} else if (
							cronParts[2] === "*" &&
							cronParts[3] === "*" &&
							cronParts[4] === "1-5"
						) {
							setSelectedPeriod("weekdays");
						} else if (
							cronParts[2] !== "*" &&
							cronParts[3] !== "*" &&
							cronParts[4] === "*"
						) {
							setSelectedPeriod("annually");
						} else if (
							cronParts[2] === "*" &&
							cronParts[3] === "*" &&
							cronParts[4].includes("#")
						) {
							setSelectedPeriod("monthly");
						} else {
							setSelectedPeriod("other");
							setCustomStepDisabled(false);
						}
					} else {
						// Default initialization if CRON parsing fails
						setTimeValue(new Date());
						setCronExpression("");
						setSelectedPeriod("daily");
					}
				} else {
					// Default initialization if CRON format is invalid
					setTimeValue(new Date());
					setCronExpression("");
					setSelectedPeriod("daily");
				}
			} else {
				// Default initialization if no CRON provided
				setTimeValue(new Date());
				setCronExpression("");
				setSelectedPeriod("daily");
			}

			setActiveStep(0);
		}
	}, [show, initialScheduleStart, initialCronExpression, serverTimezone]);

	useEffect(() => {
		// Update custom step status based on selected period
		setCustomStepDisabled(selectedPeriod !== "other");
	}, [selectedPeriod]);

	const handleNext = () => {
		// Validate current step
		if (activeStep === 0 && !startDate) {
			setSnackbarMessage("Please select a start date");
			setSnackbarOpen(true);
			return;
		}

		if (activeStep === 1) {
			if (selectedPeriod === "other") {
				// Move to custom recurrence step
				setActiveStep(2);
				return;
			} else if (!timeValue) {
				setSnackbarMessage("Please select a time");
				setSnackbarOpen(true);
				return;
			} else {
				// For non-custom recurrence, we can complete here
				handleComplete();
				return;
			}
		}

		if (activeStep === 2) {
			// Complete the wizard with custom CRON
			handleComplete();
			return;
		}

		setActiveStep((prevActiveStep) => prevActiveStep + 1);
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const handleComplete = () => {
		if (!cronExpression) {
			setSnackbarMessage("Invalid CRON expression");
			setSnackbarOpen(true);
			return;
		}

		if (!startDate) {
			setSnackbarMessage("Please select a start date");
			setSnackbarOpen(true);
			return;
		}

		setIsSubmitting(true);

		// Call the onSave callback with the created CRON expression and start date
		onSave(cronExpression, startDate);

		// Close the wizard
		setIsSubmitting(false);
		onHide(true);
	};

	const handleCancel = () => {
		onHide(false);
	};

	const handleSnackbarClose = () => {
		setSnackbarOpen(false);
	};

	const handlePeriodChange = (period: string) => {
		setSelectedPeriod(period);
		setCustomStepDisabled(period !== "other");
	};

	const handleGoBackFromCustomStep = () => {
		setActiveStep(1);
	};

	return (
		<>
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={handleCancel}
			>
				<DialogTitle className={classes.dialogTitle}>
					<Typography variant="h5" className={classes.title}>
						{activeStep === 0 && "Custom Scheduler - Start Date"}
						{activeStep === 1 && "Custom Scheduler - Select Period"}
						{activeStep === 2 && "Custom Scheduler - Advanced Settings"}
					</Typography>
				</DialogTitle>

				<DialogContent className={classes.dialogContent} dividers>
					<Stepper
						activeStep={activeStep}
						alternativeLabel
						className={classes.stepper}
						connector={<ColorlibConnector />}
					>
						{steps.map((label, index) => (
							<Step key={label}>
								<StepLabel
									StepIconProps={{
										classes: {
											root: classes.stepIcon,
											completed: classes.completedStepIcon,
										},
									}}
									className={
										index === 2 && customStepDisabled
											? classes.disabledStep
											: ""
									}
								>
									{label}
								</StepLabel>
							</Step>
						))}
					</Stepper>

					{activeStep === 0 && (
						<StartDateStep
							startDate={startDate}
							onStartDateChange={setStartDate}
						/>
					)}

					{activeStep === 1 && (
						<PeriodStep
							startDate={startDate}
							selectedPeriod={selectedPeriod}
							onPeriodChange={handlePeriodChange}
							timeValue={timeValue}
							onTimeChange={setTimeValue}
							cronExpression={cronExpression}
							onCronExpressionChange={setCronExpression}
						/>
					)}

					{activeStep === 2 && (
						<CustomRecurrenceStep
							cronExpression={cronExpression}
							onCronExpressionChange={setCronExpression}
							onGoBack={handleGoBackFromCustomStep}
						/>
					)}
				</DialogContent>

				<DialogActions className={classes.dialogActions}>
					<Grid2 container spacing={1}>
						<Grid2
							style={{ marginRight: "auto", padding: "8px 10px" }}
							size={{ xs: 4 }}
						>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={handleCancel}
							>
								Cancel
							</Button>
						</Grid2>

						{activeStep > 0 && (
							<Grid2 style={{ padding: "8px 10px" }} size={{ xs: 4 }}>
								<Button
									fullWidth
									variant="outlined"
									color="secondary"
									onClick={handleBack}
									disabled={isSubmitting}
								>
									Back
								</Button>
							</Grid2>
						)}

						<Grid2 style={{ padding: "8px 10px" }} size={{ xs: 4 }}>
							<Button
								fullWidth
								variant="contained"
								color="primary"
								onClick={handleNext}
								disabled={isSubmitting}
								startIcon={isSubmitting ? <CircularProgress size={24} /> : null}
							>
								{activeStep === steps.length - 1 ||
								(activeStep === 1 && selectedPeriod !== "other")
									? "Save"
									: "Next"}
							</Button>
						</Grid2>
					</Grid2>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbarOpen}
				autoHideDuration={5000}
				onClose={handleSnackbarClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="error" onClose={handleSnackbarClose}>
					{snackbarMessage}
				</Alert>
			</Snackbar>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
